import {
  put,
  takeLeading,
  debug,
  createAction,
  SagaIterator,
  call,
  select,
  delay,
  race,
  take,
  eventChannel,
  EventChannel,
} from "../../lib";
import { BaseRequestResult } from "../../lib/request";
import { RefreshTokenAction } from "../../lib/auth/refresh_token_action";
import { isLoginInFlight, isLogoutPending, setLogoutPending } from "../../lib/auth/logout_pending";
import { retryPendingLogout, startPendingLogout } from "../../lib/auth/pending_logout_retry";
import {
  clearLastActivity,
  isFiniteNumber,
  isIdleExpired,
  readLastActivity,
} from "../../lib/auth/idle_sign_out";
import {
  SessionOwnershipMark,
  adoptBrowserSessionScope,
  markSessionOwnership,
  readSharedSessionNonce,
  sessionOwnershipSince,
} from "../../lib/auth/session_scope";
import {
  observeSessionDeadlines,
  readSessionDeadlines,
  replyEnforcesIdle,
  startDeadlineRequest,
} from "../../lib/auth/session_deadlines";
import { toProofSafeSchemaError } from "../../model/auth_proof";

import {
  doAUTH_SET,
  doREQUEST_ERROR,
  doREQUEST_COMPLETE,
  doAUTH_REFRESHING,
  doAUTH_REFRESHING_FAILURE,
  doAUTH_REFRESH_UNAVAILABLE,
  doPROFILE_SET,
  doSETTINGS_SET,
  doPROJECT_SETTINGS_LIST_SET,
  doFAVORITE_LIST_SET,
  doIDENTITY_SET,
  doSESSION_END_REASON_SET,
  AUTH_REFRESH_RETRY_NOW,
  AUTH_REFRESHING_FAILURE,
  SessionEndReason,
} from "../state";
import {
  RefreshOutcome,
  classifyRefreshResult,
  pickSessionEndReason,
  refreshRetryDelayMs,
  serverRetryAfterMs,
} from "./refresh_outcome";

export const REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT = "REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT";
export const doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT = createAction(
  REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT,
);

export const requestIdentityRefreshTokenSubmitActionCreators = {
  doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT,
};

const REFRESH_URL = "/v1/web/identity-refresh-token-submit";

// A reducer guard's Joi error carries the whole rejected payload -- the access token included --
// inside its own `message`, so only the constraint text is ever logged, never the error itself.
const describeRefreshError = (error: unknown): string => {
  if ((error as { isJoi?: boolean } | null)?.isJoi) return toProofSafeSchemaError(error).message;
  return error instanceof Error ? `${error.name}: ${error.message}` : typeof error;
};

// `online` is a hint to try again now, never proof that the API is reachable, so winning
// the race does not reset the schedule. One channel per cycle, closed in the worker's `finally`.
const createOnlineChannel = () =>
  eventChannel<string>((emit) => {
    const listener = () => emit("online");
    window.addEventListener("online", listener);
    return () => window.removeEventListener("online", listener);
  });

interface RefreshAttemptInput {
  payload: any;
  setRequestResult: any;
  hadToken: boolean;
  // this tab's sign-ins as the cycle began
  cycleOwnership: SessionOwnershipMark;
}

interface RefreshAttemptResult {
  outcome: RefreshOutcome;
  // only a transient reply carries one, and only when it said how long to wait
  retryAfterMs?: number | null;
  // the reply's cap contradicted the one known for the sign-in, so the server is asked again
  revalidate?: boolean;
  // another tab signed in or out while the request was out, so nothing from the reply was published
  outlivedBrowserSignIn?: boolean;
}

// Only a string is an identity id. Anything else -- no identity in this tab, or a reply without one --
// means there is nothing to compare.
const identityIdOf = (identity: unknown): string | null => {
  const id = (identity as { id?: unknown } | null | undefined)?.id;
  return typeof id === "string" ? id : null;
};

// The boot found the shared input stamp past the idle limit and left the decision to the server
// (`createAppStore`), so every attempt of its cycle carries the stamp. The decision speaks only for the
// session the boot found: once this tab has signed in since, the attempt is an ordinary refresh.
const decidesIdleBoot = (payload: any, cycleOwnership: SessionOwnershipMark): boolean =>
  isFiniteNumber(payload?.bootStaleStamp?.at) &&
  sessionOwnershipSince(cycleOwnership) !== "signedIn";

// Whether, by the browser's own clock, a session the server does not end for idleness has still been
// idle past the limit. It counts from the stamp as it is now, since input in another tab may have come
// in while the boot waited, and from no earlier than the switch away from enforcement: the one on
// record for the browser's sign-in, or this reply's own when the record still says enforced. Read
// before the reply is folded into that record.
const idlePastLimitWithoutEnforcement = (sentAt: number): boolean => {
  const stamp = readLastActivity();
  if (!stamp) return false;
  const nonce = readSharedSessionNonce();
  const record = nonce ? readSessionDeadlines(null, nonce) : null;
  const switchedAt = record?.idleEnforced ? sentAt : (record?.fallbackSince ?? null);
  return isIdleExpired({ at: Math.max(stamp.at, switchedAt ?? stamp.at) }, Date.now());
};

// Ends the session the way an unconfirmed sign-out is ended: the marker ends it in every tab, and the
// logout goes out with logins waiting for it. The stamp goes so the next boot does not blame inactivity again, and a
// marker already set is left as it is, since writing it ends the session in every other open tab.
const endIdleSession = (): void => {
  clearLastActivity();
  if (!isLogoutPending()) setLogoutPending();
  startPendingLogout();
};

// Exactly one of AUTH_SET, AUTH_REFRESHING_FAILURE or AUTH_REFRESH_UNAVAILABLE follows every
// attempt, so anything waiting on the outcome actions always hears back. The one exception is a reply
// set aside because another tab signed in meanwhile: the attempt sent straight after it answers instead.
function* runRefreshAttempt({
  payload,
  setRequestResult,
  hadToken,
  cycleOwnership,
}: RefreshAttemptInput): Generator<any, RefreshAttemptResult, any> {
  let emitted: RefreshOutcome | null = null;

  try {
    // a logout this tab (or a sibling tab) could not deliver outranks any refresh. Checked
    // before EVERY attempt, so a sign-out during the retry wait is honoured on the next tick.
    // The logout itself is the shared tracked retry, not a request of the cycle's own: a login
    // waits only for that one, so a separate request here could clear the cookie it sets. The
    // retry keeps its own rules -- nothing while one is already out, a login is in progress or the
    // tab is in the background -- and the cycle ends either way. The marker retires on the
    // server's definitive answer (a 2xx, 401 or 404, see `isDefinitiveLogoutReply`).
    if (yield call(isLogoutPending)) {
      debug("saga*requestIdentityRefreshTokenSubmit honouring a pending logout");

      yield call(retryPendingLogout);

      if (hadToken) {
        const at = Date.now();
        yield put(doSESSION_END_REASON_SET({ reason: SessionEndReason.SessionEnded, at }));
      }

      // no REQUEST_ERROR: a deliberate sign-out is not an app error to surface, and nothing may
      // wait on a refresh requestId anyway
      yield put(doAUTH_REFRESHING_FAILURE());
      emitted = RefreshOutcome.Terminal;
      return { outcome: RefreshOutcome.Terminal };
    }

    yield put(doAUTH_REFRESHING());

    const ownershipAtSend = markSessionOwnership();
    const sharedNonceAtSend = readSharedSessionNonce();
    const deadlineRequest = startDeadlineRequest();
    const result: BaseRequestResult = yield call(() => RefreshTokenAction.instance.execute());
    const outcome = classifyRefreshResult(result);

    if (outcome === RefreshOutcome.Transient) {
      debug("saga*requestIdentityRefreshTokenSubmit transient refresh failure", result?.statusCode);
      return { outcome: RefreshOutcome.Transient, retryAfterMs: serverRetryAfterMs(result) };
    }

    if (outcome === RefreshOutcome.Terminal) {
      debug("saga*requestIdentityRefreshTokenSubmit terminal refresh failure", result?.statusCode);

      // only a tab that actually held a token can say "your session ended"; a fresh visit
      // that was never signed in shows nothing. A boot past the idle limit says inactivity, and writes
      // no marker: the server has already ended the session. The stamp goes so the next boot does not
      // blame inactivity again, unless another tab has refreshed it meanwhile for a session of its own.
      // Written before the auth entry is dropped, since both reducers persist into the same
      // session-storage record.
      if (decidesIdleBoot(payload, cycleOwnership)) {
        if (isIdleExpired(readLastActivity(), Date.now())) clearLastActivity();
        yield put(
          doSESSION_END_REASON_SET({ reason: SessionEndReason.Inactivity, at: Date.now() }),
        );
      } else if (hadToken) {
        const lastSecurityChange = yield select(
          (state: any) => state?.session?.lastSecurityChange ?? null,
        );
        const at = Date.now();
        yield put(
          doSESSION_END_REASON_SET({ reason: pickSessionEndReason(lastSecurityChange, at), at }),
        );
      }

      yield put(doAUTH_REFRESHING_FAILURE());
      emitted = RefreshOutcome.Terminal;

      yield put(
        doREQUEST_ERROR(
          {
            fromType: REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT,
            fromPayload: payload,
            requestId: payload.requestId,
            url: REFRESH_URL,
            ...result,
          },
          setRequestResult,
        ),
      );

      return { outcome: RefreshOutcome.Terminal };
    }

    const {
      profile,
      settings,
      projectSettingsList,
      workspaceFavoriteList,
      accessToken,
      accessTokenExpiresInSeconds,
      refreshTokenExpiresInSeconds,
      identity,
    } = result.payload;

    // Whose session the reply speaks for is settled before anything from it is published. The
    // refresh cookie is shared by every tab, so the reply can belong to a session this tab never
    // started, and ownership can change in this tab while the request is out.
    const ownershipChange = sessionOwnershipSince(ownershipAtSend);
    if (ownershipChange === "signedIn") {
      // a sign-in in this tab replaced the session the refresh was sent for, and its AUTH_SET has
      // already answered anyone waiting on this attempt
      debug("saga*requestIdentityRefreshTokenSubmit reply outlived a sign-in");
      return { outcome: RefreshOutcome.Success };
    }
    if (ownershipChange === "ended") {
      // this tab began signing out while the refresh was out: its reply cannot sign it back in
      debug("saga*requestIdentityRefreshTokenSubmit reply outlived a sign-out");
      yield put(doAUTH_REFRESHING_FAILURE());
      emitted = RefreshOutcome.Terminal;
      return { outcome: RefreshOutcome.Terminal };
    }

    // Another tab signed in or out while the request was out. The cookie the request carried may be
    // the earlier sign-in's, so the reply is bound to neither that sign-in nor this one, whoever it
    // names. Nothing is published and the caller asks again with the cookie as it is now. Without
    // storage there is no shared sign-in to compare.
    const sharedNonce = readSharedSessionNonce();
    if (
      sharedNonceAtSend !== undefined &&
      sharedNonce !== undefined &&
      sharedNonce !== sharedNonceAtSend
    ) {
      debug("saga*requestIdentityRefreshTokenSubmit reply outlived a sign-in in another tab");
      return { outcome: RefreshOutcome.Transient, outlivedBrowserSignIn: true };
    }

    // A tab that knows who it is signed in as never takes a token for someone else. Another identity
    // signed in from another tab, so this tab's session is over -- but the cookie, and the session,
    // are that identity's now, so there is no logout to send and no shared record to retire.
    const expectedIdentityId = identityIdOf(yield select((state: any) => state?.identity));
    if (expectedIdentityId && identityIdOf(identity) !== expectedIdentityId) {
      debug("saga*requestIdentityRefreshTokenSubmit reply is for another identity");
      yield put(
        doSESSION_END_REASON_SET({ reason: SessionEndReason.SessionEnded, at: Date.now() }),
      );
      yield put(doAUTH_REFRESHING_FAILURE());
      emitted = RefreshOutcome.Terminal;
      return { outcome: RefreshOutcome.Terminal };
    }

    // A boot past the idle limit decides here, before the reply joins the browser's sign-in or reaches
    // its shared deadlines, both of which other tabs act on. A server that ends idle sessions counts
    // work in Explorer as well, so its reply stands and the tab signs in. Otherwise the session ends as an
    // unconfirmed sign-out is ended (`endIdleSession`), unless input or the switch away from enforcement is recent, or a login
    // is on the wire, whose new session the marker would end.
    if (
      decidesIdleBoot(payload, cycleOwnership) &&
      !replyEnforcesIdle(result.payload) &&
      !isLoginInFlight() &&
      idlePastLimitWithoutEnforcement(deadlineRequest.at)
    ) {
      debug("saga*requestIdentityRefreshTokenSubmit ending a session idle past the limit");
      yield call(endIdleSession);
      yield put(doSESSION_END_REASON_SET({ reason: SessionEndReason.Inactivity, at: Date.now() }));
      // no REQUEST_ERROR, as for a pending logout: the sign-out is deliberate
      yield put(doAUTH_REFRESHING_FAILURE());
      emitted = RefreshOutcome.Terminal;
      return { outcome: RefreshOutcome.Terminal };
    }

    const scope = adoptBrowserSessionScope();
    const { deadlines: sessionDeadlines, revalidate } = observeSessionDeadlines({
      known: yield select((state: any) => state?.auth?.sessionDeadlines ?? null),
      nonce: scope.nonce,
      payload: result.payload,
      request: deadlineRequest,
    });

    yield put(
      doAUTH_SET({
        accessToken,
        accessTokenExpiresInSeconds,
        refreshTokenExpiresInSeconds,
        sessionNonce: scope.nonce,
        sessionDeadlines,
      }),
    );
    emitted = RefreshOutcome.Success;

    yield put(doPROFILE_SET(profile || {}));
    if (settings) yield put(doSETTINGS_SET(settings));
    yield put(doPROJECT_SETTINGS_LIST_SET(projectSettingsList || []));
    yield put(doFAVORITE_LIST_SET(workspaceFavoriteList || []));
    if (identity) yield put(doIDENTITY_SET(identity));

    yield put(
      doREQUEST_COMPLETE(
        {
          fromType: REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT,
          requestId: payload.requestId,
          ...result,
        },
        setRequestResult,
      ),
    );

    return { outcome: RefreshOutcome.Success, revalidate };
  } catch (error) {
    // a reducer guard on a half-shaped 200, a serialization check, anything: keep the token,
    // keep the watcher, and let the schedule try again. Never let it escape: `createAppStore`'s
    // saga `onError` re-throws, which under `takeLeading` would kill the one watcher and leave
    // the tab unable to refresh for the rest of its life.
    debug("saga*requestIdentityRefreshTokenSubmit unexpected error", error);
    console.error(
      "saga*requestIdentityRefreshTokenSubmit unexpected error:",
      describeRefreshError(error),
    );
    return { outcome: emitted ?? RefreshOutcome.Transient };
  }
}

export function* requestIdentityRefreshTokenSubmit({
  payload,
  setRequestResult,
}: any): SagaIterator {
  debug("saga*requestIdentityRefreshTokenSubmit");

  // captured once for the whole cycle: whether this tab ever held a token decides whether a
  // terminal outcome is worth explaining on the login page
  const hadToken = Boolean(yield select((state: any) => state?.auth?.accessToken));
  // and so are this tab's sign-ins: a boot's decision never ends a session started in this tab since
  const cycleOwnership = markSessionOwnership();

  const onlineChannel: EventChannel<string> = yield call(createOnlineChannel);

  try {
    let attempt = 0;
    let revalidated = false;
    let askedAfterBrowserSignIn = false;

    for (;;) {
      attempt += 1;

      const { outcome, retryAfterMs, revalidate, outlivedBrowserSignIn } = yield* runRefreshAttempt(
        {
          payload,
          setRequestResult,
          hadToken,
          cycleOwnership,
        },
      );
      // a contradicted cap is asked about once, straight away, and that answer stands
      if (outcome === RefreshOutcome.Success && revalidate && !revalidated) {
        revalidated = true;
        continue;
      }
      // so is a reply that outlived another tab's sign-in; that attempt published nothing, so the
      // next one is what anyone waiting on the outcome hears
      if (outlivedBrowserSignIn && !askedAfterBrowserSignIn) {
        askedAfterBrowserSignIn = true;
        continue;
      }
      if (outcome !== RefreshOutcome.Transient) return;

      const wait = refreshRetryDelayMs(attempt, retryAfterMs);
      // the transient action is the only thing dispatched -- no REQUEST_ERROR, so a long
      // outage cannot grow one request-state entry per retry
      yield put(doAUTH_REFRESH_UNAVAILABLE({ attempt, nextRetryAt: Date.now() + wait }));

      const { sessionEnded } = yield race({
        // the schedule, the browser saying it is back, and the user's "Retry now"
        scheduled: delay(wait),
        online: take(onlineChannel),
        retryNow: take(AUTH_REFRESH_RETRY_NOW),
        // a 401-triggered join: an ordinary 401 proves the API is reachable, so go now. The 10s
        // request timeout bounds how often user traffic can force an attempt.
        rejoin: take(REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT),
        // this cycle only emits AUTH_REFRESHING_FAILURE from inside an attempt, so one arriving
        // here means the session was ended elsewhere (a sibling tab's sign-out). Retrying after
        // that would send this tab's own logout and could land on top of a fresh login.
        sessionEnded: take(AUTH_REFRESHING_FAILURE),
      });
      if (sessionEnded) return;
    }
  } finally {
    onlineChannel.close();
  }
}

export function* identityRefreshTokenSubmitSaga() {
  // one cycle at a time. A dispatch that arrives while a cycle runs is dropped, and its
  // dispatcher waits on the outcome actions instead of on a requestId.
  yield takeLeading(REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT, requestIdentityRefreshTokenSubmit);
}
